import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type { ConversationTurnPresentation } from './state'

export type ConversationNavigationItem = { key: string; title: string; preview: string }

function excerpt(text: string, limit: number): string {
  const plain = text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]*>/g, '')
    .replace(/^[\s]*[>#*-]+\s*/gm, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return plain.length > limit ? `${plain.slice(0, limit - 1).trimEnd()}…` : plain
}

export function conversationNavigationItems(
  turns: ReadonlyArray<ConversationTurnPresentation>
): Array<ConversationNavigationItem> {
  return turns.flatMap((turn) => {
    if (turn.originMessage?.author !== 'user') return []
    const response = [...turn.resultMessages]
      .reverse()
      .find((message) => message.author === 'assistant' && message.content.trim())
    const fallback = {
      submitted: 'Preparing your request',
      working: 'Working',
      waiting: 'Waiting for your answer',
      writing: 'Writing the response',
      stopped: 'Work stopped',
      complete: 'No response text'
    }[turn.phase]
    return [
      {
        key: turn.key,
        title: excerpt(turn.originMessage.content, 160) || 'Untitled request',
        preview: excerpt(response?.content || turn.questions.at(-1)?.prompt || turn.activeSummary || fallback, 240)
      }
    ]
  })
}

export function readingTurnKey(
  offsets: ReadonlyArray<{ key: string; top: number }>,
  scrollTop: number,
  viewportHeight: number,
  scrollHeight: number
): string | undefined {
  if (scrollHeight - scrollTop - viewportHeight <= 2) return offsets.at(-1)?.key
  const readingLine = scrollTop + Math.min(96, viewportHeight / 4)
  for (let index = offsets.length - 1; index >= 0; index--) {
    if (offsets[index]!.top <= readingLine) return offsets[index]!.key
  }
  return offsets[0]?.key
}

export function visibleTurnKeys(
  bounds: ReadonlyArray<{ key: string; top: number; bottom: number }>,
  scrollTop: number,
  viewportHeight: number
): Array<string> {
  if (viewportHeight <= 0) return []
  return bounds
    .filter((turn) => turn.bottom > scrollTop && turn.top < scrollTop + viewportHeight)
    .map((turn) => turn.key)
}

type NavigationPosition = { key: string | undefined; visibleKeys: Array<string>; canJumpToEnd: boolean }
const emptyPosition: NavigationPosition = { key: undefined, visibleKeys: [], canJumpToEnd: false }

// Observe the transcript, not the window: canvas panels resize chat independently.
// A frame coalesces scrolling, streamed layout changes, and history prepends.
// Only changes to the reading turn, visible set, or end proximity enter React state.
export function useConversationNavigationPosition(
  scrollerRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLDivElement | null>,
  itemKeys: string,
  threadId: number | undefined
): NavigationPosition {
  const [reading, setReading] = useState<NavigationPosition & { threadId: number | undefined }>()
  useEffect(() => {
    const scroller = scrollerRef.current
    const content = contentRef.current
    if (scroller === null || content === null) return
    const keys = new Set(itemKeys.split('\n'))
    let frame: number | undefined
    const measure = () => {
      frame = undefined
      const top = scroller.getBoundingClientRect().top
      const offsets = Array.from(content.querySelectorAll<HTMLElement>('[data-conversation-turn-key]'))
        .filter((element) => keys.has(element.dataset['conversationTurnKey']!))
        .map((element) => {
          const bounds = element.getBoundingClientRect()
          return {
            key: element.dataset['conversationTurnKey']!,
            top: bounds.top - top + scroller.scrollTop,
            bottom: bounds.bottom - top + scroller.scrollTop
          }
        })
      const key = readingTurnKey(offsets, scroller.scrollTop, scroller.clientHeight, scroller.scrollHeight)
      const visibleKeys = visibleTurnKeys(offsets, scroller.scrollTop, scroller.clientHeight)
      const canJumpToEnd = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 24
      setReading((previous) =>
        previous !== undefined &&
        previous.threadId === threadId &&
        previous.key === key &&
        previous.canJumpToEnd === canJumpToEnd &&
        previous.visibleKeys.length === visibleKeys.length &&
        previous.visibleKeys.every((visibleKey, index) => visibleKey === visibleKeys[index])
          ? previous
          : { threadId, key, visibleKeys, canJumpToEnd }
      )
    }
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(measure)
    }
    const observer = new ResizeObserver(schedule)
    observer.observe(scroller)
    observer.observe(content)
    scroller.addEventListener('scroll', schedule, { passive: true })
    schedule()
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', schedule)
      if (frame !== undefined) cancelAnimationFrame(frame)
    }
  }, [contentRef, scrollerRef, itemKeys, threadId])
  return reading !== undefined && reading.threadId === threadId ? reading : emptyPosition
}
